import { useState, type ReactNode } from "react";
import { Plus } from "lucide-react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createPlatform, getPlatformCatalog, listPlatforms, updatePlatformStatus } from "@/services/channelCenter";
import type { PlatformSummary } from "@/services/types/channelCenter";
import { useListingAgentEnabled } from "@/services/listingJobs";
import { PLATFORM_BRAND, relativeTime } from "./channelCenterConstants";
import { ListingAgentsBlock } from "./ListingAgentsBlock";

function Pill({ tone, children }: { tone: "green" | "amber" | "gray" | "red"; children: React.ReactNode }) {
  const cls = {
    green: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
    gray: "bg-slate-100 text-slate-600",
    red: "bg-rose-50 text-rose-600",
  }[tone];
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[12px] font-medium ${cls}`}>
      <span className="h-[7px] w-[7px] rounded-full bg-current" />
      {children}
    </span>
  );
}

function Switch({ on, onToggle }: { on: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      title={on ? "status=live（我方交易闸门）" : "status=inactive"}
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      className={`relative ml-auto h-[22px] w-10 shrink-0 rounded-full transition-colors ${on ? "bg-blue-600" : "bg-slate-300"}`}
    >
      <span
        className={`absolute top-0.5 h-[18px] w-[18px] rounded-full bg-white shadow transition-all ${on ? "left-5" : "left-0.5"}`}
      />
    </button>
  );
}

function GygCard({ platform, onToggle }: { platform: PlatformSummary; onToggle: () => void }) {
  const navigate = useNavigate();
  const brand = PLATFORM_BRAND[platform.code] ?? { abbr: platform.code.slice(0, 3).toUpperCase(), bg: "#64748B" };
  return (
    <div
      data-testid={`platform-card-${platform.code}`}
      onClick={() => navigate(`/admin/api-integration/${platform.code}`)}
      className="cursor-pointer rounded-[14px] border border-slate-200 bg-white p-[18px] transition hover:-translate-y-px hover:shadow-lg"
    >
      <div className="mb-2.5 flex items-center gap-2.5">
        <div
          className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[9px] text-[13px] font-bold text-white"
          style={{ background: brand.bg }}
        >
          {brand.abbr}
        </div>
        <div className="min-w-0">
          <div className="truncate text-[15px] font-semibold text-slate-900">{platform.display_name}</div>
          <div className="truncate text-[12px] text-slate-400">
            {platform.supplier_id ? `Supplier ID ${platform.supplier_id}` : "未配置 Supplier ID"}
          </div>
        </div>
        <Switch on={platform.status === "live"} onToggle={onToggle} />
      </div>

      <div className="flex flex-wrap gap-1.5">
        <Pill tone={platform.status === "live" ? "green" : "gray"}>
          {platform.status === "live" ? "测试环境已认证" : "未开通"}
        </Pill>
        <Pill tone="gray">{platform.environment === "sandbox" ? "production 未开通" : "production"}</Pill>
      </div>

      <div className="mt-2.5 flex flex-col gap-0.5">
        {platform.cert_note && (
          <div className="flex justify-between py-0.5 text-[12.5px] text-slate-500">
            <span>入站认证自测</span>
            <b className="font-semibold text-slate-900">{platform.cert_note}</b>
          </div>
        )}
        <div className="flex justify-between py-0.5 text-[12.5px] text-slate-500">
          <span>出站推送（{platform.environment === "sandbox" ? "沙箱" : "生产"}）</span>
          <b className="font-semibold text-slate-900 tabular-nums">
            {platform.outbound_sent_count} 条成功
            {platform.outbound_failed_count > 0 ? ` · ${platform.outbound_failed_count} 条失败` : ""}
            {(platform.outbound_paused_count ?? 0) > 0 ? ` · ${platform.outbound_paused_count} 个编号暂停` : ""}
          </b>
        </div>
        <div className="flex justify-between py-0.5 text-[12.5px] text-slate-500">
          <span>推送产品</span>
          <b className="font-semibold text-slate-900 tabular-nums">
            {platform.mapped_product_count} / {platform.total_product_count}
          </b>
        </div>
        <div className="flex justify-between py-0.5 text-[12.5px] text-slate-500">
          <span>最近同步</span>
          <b className="font-semibold text-slate-900 tabular-nums">{relativeTime(platform.last_sync_at)}</b>
        </div>
      </div>
    </div>
  );
}

function StaticViatorCard() {
  return (
    <div className="rounded-[14px] border border-slate-200 bg-white p-[18px]">
      <div className="mb-2.5 flex items-center gap-2.5">
        <div className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[9px] bg-[#33AA66] text-[13px] font-bold text-white">
          V
        </div>
        <div className="min-w-0">
          <div className="text-[15px] font-semibold text-slate-900">Viator</div>
          <div className="text-[12px] text-slate-400">TripAdvisor 系</div>
        </div>
        <span className="ml-auto h-[22px] w-10 shrink-0 rounded-full bg-slate-300" title="未开通" />
      </div>
      <Pill tone="amber">邮件解析通道（规划中）</Pill>
      <div className="mt-2.5 flex flex-col gap-0.5">
        <div className="flex justify-between py-0.5 text-[12.5px] text-slate-500">
          <span>官方 API</span>
          <b className="font-semibold text-slate-900">无自助沙箱 · 商务卡点</b>
        </div>
        <div className="flex justify-between py-0.5 text-[12.5px] text-slate-500">
          <span>对冲方案</span>
          <b className="font-semibold text-slate-900">V1 邮件建单（待样本）</b>
        </div>
      </div>
    </div>
  );
}

function StaticScoutingCard() {
  return (
    <div className="rounded-[14px] border border-dashed border-slate-200 bg-white p-[18px]">
      <div className="mb-2.5 flex items-center gap-2.5">
        <div className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[9px] bg-slate-400 text-[13px] font-bold text-white">
          …
        </div>
        <div className="min-w-0">
          <div className="text-[15px] font-semibold text-slate-900">Klook / Civitatis / Holibob / Musement / Tiqets</div>
          <div className="text-[12px] text-slate-400">侦查中（PLATRECON 工单）</div>
        </div>
      </div>
      <Pill tone="gray">待可行性报告后逐家开卡</Pill>
      <div className="mt-2.5 text-[12.5px] text-slate-500">加平台 = 加一行配置 + 一个适配器</div>
    </div>
  );
}

export function ChannelCenterPage() {
  // LISTCTR-1：「上架中心」已拆成独立模块（/admin/listing）；旧链接 ?tab=listing 跳过去
  const [sp] = useSearchParams();
  const [showNew, setShowNew] = useState(false);
  const qc = useQueryClient();
  const listingAgentEnabled = useListingAgentEnabled();

  const platformsQ = useQuery({
    queryKey: ["channel-center", "platforms"],
    queryFn: listPlatforms,
  });
  // 🔴 深链导航 bug 根因（2026-08-07 架构师复现·中期预审后修复）：全局 QueryClient
  // retry:1（main.tsx）——首次请求失败到重试之间有一段 isLoading=false 且
  // isError=false 但 data 仍 undefined 的窗口，此时用 data! 断言取值会真崩溃
  // （不是只在本地代理 404 才炸，任何瞬时失败/首次部署窗口都会命中）。
  // 跟 SuppliersList.tsx 的 `data ?? []` 同一套防御写法，不再用非空断言。
  const platforms = platformsQ.data ?? [];

  const toggleMut = useMutation({
    mutationFn: ({ code, status }: { code: string; status: "live" | "inactive" }) => updatePlatformStatus(code, status),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["channel-center", "platforms"] }),
  });

  function handleToggle(p: PlatformSummary) {
    const next = p.status === "live" ? "inactive" : "live";
    const msg =
      next === "live"
        ? "开启后该平台可实时下单，确认开启？"
        : `确认关闭 ${p.display_name} 交易闸门？关闭后该平台的新订单将全部被拒（NO_AVAILABILITY）。`;
    if (!window.confirm(msg)) return;
    toggleMut.mutate({ code: p.code, status: next });
  }

  if (sp.get("tab") === "listing") return <Navigate to="/admin/listing" replace />;

  return (
    <div className="flex flex-col gap-1 p-6 lg:p-8">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">渠道中心</h1>
          <span className="text-[13px] text-slate-500">各 OTA 平台的连接、凭据、开闸与同步日志 · 仅老板与技术管理员可见 · 产品勾选上架在左侧菜单「上架中心」</span>
        </div>
        {/* 🔴 PLATNEW：这张表以前没有任何创建通道（migration 无 seed / API 无 POST /
            UI 无按钮），新部署的主体渠道中心必然是空的，平台无从配起。 */}
        <button
          type="button"
          data-testid="chanctr-new-platform"
          onClick={() => setShowNew(true)}
          className="flex h-9 shrink-0 items-center gap-1.5 rounded-lg bg-blue-600 px-3 text-[13px] font-semibold text-white hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          新增平台
        </button>
      </header>

      {(
        <div className="mt-5 flex flex-col gap-3.5">
          <div className="text-[13px] text-slate-500">平台连接状态一览</div>

          {platformsQ.isLoading ? (
            <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-[13px] text-slate-500">加载中...</div>
          ) : platformsQ.isError ? (
            <button
              type="button"
              onClick={() => platformsQ.refetch()}
              className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-left text-[13px] font-medium text-rose-700 hover:bg-rose-100"
            >
              加载失败，点击重试
            </button>
          ) : (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(320px,1fr))] gap-4">
              {platforms.map((p) => (
                <GygCard key={p.code} platform={p} onToggle={() => handleToggle(p)} />
              ))}
              <StaticViatorCard />
              <StaticScoutingCard />
            </div>
          )}

          {/* LIST-3a · ④ 代理机块——开关关着（listing_agent 未启用）整块不渲染（工单 §10）。 */}
          {listingAgentEnabled && <ListingAgentsBlock />}
        </div>
      )}

      {showNew && (
        <NewPlatformModal
          onClose={() => setShowNew(false)}
          onCreated={() => {
            qc.invalidateQueries({ queryKey: ["channel-center", "platforms"] });
            setShowNew(false);
          }}
        />
      )}
    </div>
  );
}

/**
 * 新增平台连接 Modal（PLATNEW·2026-09-10）
 *
 * 🔴 这里**不收任何凭据**——建完行跳详情页，用页面上既有的「轮换入站凭据」/
 * 「更新出站凭据」去配。密码不走通用创建路径。
 */
function NewPlatformModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [outboundUrl, setOutboundUrl] = useState("");
  const [err, setErr] = useState<string | null>(null);

  // 用户裁决 A（2026-09-10）：平台固定 → 代号走白名单下拉，不自由输入。
  // 目录来自后端 GET /platform-catalog，前端不另抄一份（一处真源）。
  const catalogQ = useQuery({ queryKey: ["channel-center", "platform-catalog"], queryFn: getPlatformCatalog });
  const catalog = catalogQ.data ?? [];
  const picked = catalog.find((c) => c.code === code);
  const valid = !!picked && !picked.already_added;

  const mut = useMutation({
    mutationFn: () =>
      createPlatform({
        code,
        display_name: displayName.trim() || null,
        platform_supplier_id: supplierId.trim() || null,
        outbound_base_url: outboundUrl.trim() || null,
      }),
    onSuccess: (p) => {
      onCreated();
      navigate(`/admin/api-integration/${p.code}`);   // 直接进详情页配凭据
    },
    onError: (e: unknown) => setErr(e instanceof Error ? e.message : "创建失败，请重试"),
  });

  const F = ({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) => (
    <div>
      <label className="text-[12px] font-medium text-slate-600">{label}</label>
      {children}
      {hint && <p className="mt-1 text-[11px] text-slate-400">{hint}</p>}
    </div>
  );
  const inputCls = "mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-[13px] outline-none focus:border-blue-500";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm animate-in fade-in">
      <div className="w-full max-w-lg rounded-xl border border-slate-200 bg-white p-6 shadow-xl animate-in zoom-in-95 slide-in-from-bottom-4">
        <h2 className="text-lg font-semibold text-slate-900">新增平台连接</h2>
        <p className="mt-1 text-[13px] text-slate-500">
          先建一行配置，建完自动进详情页配凭据。新平台一律是<b>关闭</b>状态，测通了再手动开。
        </p>

        <div className="mt-4 flex flex-col gap-3">
          <F label="平台 *" hint={picked ? picked.note : "只能选已接好适配器的平台 · 要接新平台请起工程工单"}>
            <select
              data-testid="chanctr-new-platform-code"
              value={code}
              onChange={(e) => { setCode(e.target.value); setDisplayName(""); }}
              className={inputCls}
              disabled={catalogQ.isLoading}
            >
              <option value="">{catalogQ.isLoading ? "加载目录…" : "选择平台…"}</option>
              {catalog.map((c) => (
                <option key={c.code} value={c.code} disabled={c.already_added}>
                  {c.label}（{c.code}）{c.already_added ? " · 已添加" : ""}
                </option>
              ))}
            </select>
            {catalogQ.isError && <p className="mt-1 text-[11px] text-rose-500">目录加载失败，请重试</p>}
          </F>
          <F label="显示名称" hint="留空用默认名">
            <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder={picked?.label ?? ""} className={inputCls} />
          </F>
          {picked?.capabilities.needs_supplier_id && (
            <F label="平台侧供应商 ID" hint="选填 · 对方给的 supplier / vendor 编号">
              <input value={supplierId} onChange={(e) => setSupplierId(e.target.value)} className={inputCls} />
            </F>
          )}
          {picked?.capabilities.outbound && (
            <F label="出站根地址" hint="选填 · 留空则该平台不出站。生产地址请填对方的正式域名">
              <input value={outboundUrl} onChange={(e) => setOutboundUrl(e.target.value)} placeholder="https://supplier-api.example.com" className={inputCls} />
            </F>
          )}
        </div>

        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
          🔑 <b>这里不填密码</b>。建完在详情页用「轮换入站凭据」生成我方给对方的账号密码
          {picked?.capabilities.outbound && "，用「更新出站凭据」录对方给我们的账号密码"}。
        </div>

        {err && <p className="mt-3 rounded-md bg-rose-50 px-3 py-2 text-[12px] font-medium text-rose-700">{err}</p>}

        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-md border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">取消</button>
          <button
            data-testid="chanctr-new-platform-confirm"
            disabled={!valid || mut.isPending}
            onClick={() => { setErr(null); mut.mutate(); }}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {mut.isPending ? "创建中…" : "创建"}
          </button>
        </div>
      </div>
    </div>
  );
}
